/**
 * Type definitions for the Infrastructure (Docker control plane) area.
 *
 * These mirror the API service contracts for the `docker-hosts` and
 * `client-instances` Feathers services (Phase 2/3 API). The Infrastructure area
 * reads these through registered `@intrasync/stores` ServiceStores (declared in
 * serviceStores/infrastructure.stores.ts); the types remain local to this views
 * module because the API does not export them.
 *
 * SECRET HANDLING (mirrors the DevOps PAT rule): a Docker host is a Portainer
 * endpoint authenticated with a Portainer API key. That key — like the Azure
 * PAT and webhook secrets — is encrypted at rest by the API and is NEVER
 * serialised to the client. The host record carries only a `hasApiKey` presence
 * boolean, never the secret itself. The form's write-only `apiKey` field is
 * submitted on save and cleared after; it is never repopulated from a record.
 */

// ---- Docker hosts (Portainer endpoints) -----------------------------------

export type DockerHostStatus =
  // The API health probe writes one of these. "up"/"online" = reachable &
  // healthy; "down"/"offline"/"error" = unreachable; "unknown" = not yet probed.
  | "up"
  | "online"
  | "healthy"
  | "down"
  | "offline"
  | "error"
  | "unknown"
  | string;

/**
 * A configured Portainer endpoint (Docker host) — backed by the
 * `docker_hosts` table. The `encryptedApiKey` column is NEVER serialised to the
 * client; the API masks it and returns a `hasApiKey` boolean so the UI can show
 * whether a key is on file without ever exposing it.
 */
export interface DockerHost {
  id: string;
  /** The portainer_connections row this endpoint was discovered through. */
  portainerConnectionId?: string | null;
  /** Denormalized portainer_connections.name (the owning Portainer server). */
  connectionName?: string | null;
  /** Friendly label for the endpoint. */
  name: string;
  /** Base Portainer URL, e.g. https://portainer.example.com. */
  portainerUrl: string;
  /** Portainer environment/endpoint id within that Portainer instance. */
  endpointId?: number | string | null;
  /** Write-only in the UI; the API never returns the API key itself. */
  hasApiKey?: boolean;
  /** Latest health probe outcome. */
  status?: DockerHostStatus;
  /** Free-text detail from the last probe (e.g. an error message). */
  statusMessage?: string | null;
  lastSeenAt?: string | null;
  /** Container/instance counts surfaced by the API health probe. */
  containerCount?: number | null;
  runningCount?: number | null;
  /** Portainer/Docker engine version reported by the endpoint. */
  engineVersion?: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  deletedAt?: string | null;
}

/** Success statuses for a Docker host (reachable & healthy). */
export const DOCKER_HOST_UP_STATUSES: ReadonlyArray<string> = [
  "up",
  "online",
  "healthy",
];

export const isDockerHostUp = (status?: DockerHostStatus): boolean =>
  !!status && DOCKER_HOST_UP_STATUSES.includes(status);

export const DOCKER_HOST_DOWN_STATUSES: ReadonlyArray<string> = [
  "down",
  "offline",
  "error",
];

export const isDockerHostDown = (status?: DockerHostStatus): boolean =>
  !!status && DOCKER_HOST_DOWN_STATUSES.includes(status);

// ---- Portainer connections (credential registry) --------------------------

export type PortainerConnectionStatus =
  | "unverified"
  // The API's `validate` action writes "verified" on success (not "valid");
  // both are treated as the success state throughout the UI.
  | "verified"
  | "valid"
  | "invalid"
  | "error"
  | string;

/**
 * A configured Portainer server — backed by the `portainer_connections` table.
 * The `encryptedApiKey` column is NEVER serialised to the client; the API masks
 * it and returns a `hasApiKey` boolean so the UI can show whether a key is on
 * file without ever exposing it.
 */
export interface PortainerConnection {
  id: string;
  name: string;
  /** Base URL of the Portainer CE API, e.g. https://portainer.internal:9443. */
  baseUrl: string;
  /** Write-only in the UI; the API never returns the key itself. */
  hasApiKey?: boolean;
  lastValidatedAt?: string | null;
  status?: PortainerConnectionStatus;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  deletedAt?: string | null;
}

/**
 * Editable subset of a connection used by the create/edit form. `apiKey` is
 * write-only — it is sent to the API on save but never read back.
 */
export interface PortainerConnectionFormData {
  name?: string;
  baseUrl?: string;
  /** Write-only — submitted to the API, never rendered back. */
  apiKey?: string;
}

/**
 * Raw response of the connection `validate` custom action. The API exposes
 * validation via `create` (POST) with `{ action: 'validate', id }` and returns
 * the full record with its top-level `status` ("verified" | "invalid") +
 * `lastValidatedAt`, plus a nested `validation` object carrying the endpoint
 * count / failure message. No secret is ever sent (only the action + id).
 */
export interface PortainerConnectionValidateResponse {
  status: PortainerConnectionStatus;
  lastValidatedAt?: string | null;
  validation?: {
    ok: boolean;
    status: PortainerConnectionStatus;
    endpointCount?: number;
    message?: string;
  };
}

/** Normalised (flat) result of the connection `validate` action for the UI alert. */
export interface PortainerConnectionValidateResult {
  status: PortainerConnectionStatus;
  message?: string;
  endpointCount?: number;
  lastValidatedAt?: string | null;
}

/** Payload sent to trigger the `validate` action (POST, no secret). */
export interface PortainerConnectionValidatePayload {
  action: "validate";
  id: string;
}

/** Success statuses for a connection (the API writes "verified"; UI also accepts "valid"). */
export const PORTAINER_SUCCESS_STATUSES: ReadonlyArray<string> = ["verified", "valid"];

export const isPortainerConnectionValid = (
  status?: PortainerConnectionStatus
): boolean => !!status && PORTAINER_SUCCESS_STATUSES.includes(status);

// ---- Azure Container Registry (Phase 2) -----------------------------------

export type RegistryAuthMethod = "admin" | "service-principal";

/**
 * A configured ACR registry — backed by `registry_connections`. The credential
 * (admin password or service-principal secret) is encrypted at rest and NEVER
 * serialised to the client; the API returns only `hasCredential` + a mask.
 */
export interface RegistryConnection {
  id: string;
  name: string;
  /** ACR login server, e.g. intrasync.azurecr.io. */
  loginServer: string;
  authMethod: RegistryAuthMethod;
  /** admin username OR service-principal client (application) id. */
  username?: string | null;
  /** AAD tenant id (service-principal only). */
  tenantId?: string | null;
  /** True when a credential is on file (the secret itself is never returned). */
  hasCredential?: boolean;
  credentialMasked?: string | null;
  lastValidatedAt?: string | null;
  status?: PortainerConnectionStatus;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  /** Only present as the result of a `validate` action. */
  validation?: {
    ok: boolean;
    status: string;
    repositoryCount?: number;
    message?: string;
  };
}

/** Editable subset for the registry create/edit form. `password` is write-only. */
export interface RegistryConnectionFormData {
  name?: string;
  loginServer?: string;
  authMethod?: RegistryAuthMethod;
  username?: string;
  tenantId?: string;
  /** Write-only — submitted to the API, never rendered back. */
  password?: string;
}

/** A repository name from the read-through `registry-repositories` service. */
export interface RegistryRepository {
  name: string;
  registryConnectionId: string;
  /** True when served from the local cache because live ACR was unreachable. */
  stale?: boolean;
}

/** A tag from the read-through `registry-tags` service (newest first). */
export interface RegistryTag {
  registryConnectionId: string;
  repository: string;
  name: string;
  digest: string;
  createdTime?: string;
  lastUpdateTime?: string;
  stale?: boolean;
}

// ---- Client instances (containers) ----------------------------------------

/**
 * The two container roles every managed client runs: the API service and the
 * web front end. The control plane lists exactly these two per client.
 */
export type ClientInstanceRole = "api" | "web" | string;

export type ClientInstanceStatus =
  // Mirrors Docker container states surfaced via Portainer. 'missing' is the
  // API's stale-lifecycle state: the cached row vanished from discovery (the
  // container was removed outside the control plane). Missing rows are kept
  // for 7 days (actions blocked), then soft-deleted by the reconcile sweep.
  | "running"
  | "healthy"
  | "starting"
  | "restarting"
  | "paused"
  | "exited"
  | "stopped"
  | "dead"
  | "created"
  | "missing"
  | "unknown"
  | string;

/**
 * Docker HEALTHCHECK state for the container, when it defines one. The API
 * parses '(healthy)'/'(unhealthy)'/'(health: starting)' from the list Status
 * text and reads `State.Health.Status` on inspect. Null/undefined = the
 * container has no healthcheck (not the same as unhealthy).
 */
export type ClientInstanceHealth =
  | "healthy"
  | "unhealthy"
  | "starting"
  | string;

/**
 * A single deployed container for a client — backed by the `client_instances`
 * table / `client-instances` service. Each managed client has TWO of these
 * (role `api` + role `web`), each running on a {@link DockerHost}.
 *
 * Read-only in this phase: status/image/digest are reported by the API from the
 * live Portainer endpoint. Start/stop/deploy actions land in Phase 5.
 */
export interface ClientInstance {
  id: string;
  /** Owning client (FK → clients.id). Null for discovered-but-unassigned
   *  containers (assignmentState 'unassigned') — always guard before use. */
  clientId: string | null;
  /** Denormalised client label, if the API joins it in. */
  clientName?: string | null;
  /** Which of the client's two containers this row is. */
  role: ClientInstanceRole;
  /** Host the container runs on (FK → docker_hosts.id). */
  dockerHostId?: string | null;
  dockerHostName?: string | null;
  /** Portainer/Docker container id (and name) for the live container. */
  containerId?: string | null;
  containerName?: string | null;
  /** Current container status reported from Portainer. */
  status?: ClientInstanceStatus;
  /** Docker lifecycle word ('running' | 'exited' | ...), aliasing status. */
  state?: string | null;
  /** Docker HEALTHCHECK state, when the container defines one. */
  health?: ClientInstanceHealth | null;
  /** Operator action gate (client_instances.enabled). False blocks every
   *  mutating action API-side; toggled via the 'enable'/'disable' actions. */
  enabled?: boolean;
  /** Set while status === 'missing': when discovery first stopped seeing it. */
  missedSince?: string | null;
  /** Container restart count (detail/inspect only). */
  restartCount?: number;
  /** Deployed image reference, e.g. registry/intrasync-api:26.6.2. CANONICAL
   *  API field name — the `client-instances` service returns `imageRef`. */
  imageRef?: string | null;
  /** Resolved image digest (sha256:...) for the running container. CANONICAL
   *  API field name — the service returns `currentDigest`. */
  currentDigest?: string | null;
  /** When the API last observed this container's state. */
  lastSeenAt?: string | null;
  /** When the running container was started. */
  startedAt?: string | null;
  // ---- update-availability tracking (Phase 3) ----------------------------
  /** True when a newer image is available in the registry per the track policy. */
  updateAvailable?: boolean;
  /** The latest available image ref for this instance's repository. */
  latestImageRef?: string | null;
  /** The digest of that latest available image. */
  latestDigest?: string | null;
  /** When the registry was last checked for this instance. */
  latestCheckedAt?: string | null;
  /** How update-availability is judged: 'same-tag' | 'semver' | 'manual' | null. */
  trackPolicy?: string | null;
  /** When updateAvailable first became true (for stale-update surfacing). */
  updateAvailableSince?: string | null;
  /** Detail only: what the container's START COMMAND does about database
   *  migrations (#2579). The command only — no client app reports its
   *  migration state, so this is never a claim about the database. */
  startMigrations?: InstanceStartMigrations;
  // ---- assignment (Phase 4) ----------------------------------------------
  /** DB override owner set by an operator on an unlabeled container. */
  assignedClientId?: string | null;
  /** Derived ownership: 'labelled' | 'assigned-override' | 'unassigned' |
   *  'label-conflict'. */
  assignmentState?: string | null;
  /** Operator designation: IntraSync's own infrastructure, not a client's. */
  isInternal?: boolean;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

/** What the start command does about migrations (the API's start-migrations.ts). */
export interface InstanceStartMigrations {
  mode: "runs-at-start" | "none-in-start-command" | "script-hq-cannot-see" | "unknown";
  /** The migration steps the command names, as written. */
  steps: string[];
  /** Every step must succeed before the server starts (`a && b && server`). */
  gatesServer: boolean;
  command: string | null;
}

/** True when a newer image is available for this instance. */
export const hasUpdateAvailable = (i?: ClientInstance | null): boolean =>
  !!i && i.updateAvailable === true;

/** True when a container is discovered but not yet assigned to a client. */
export const isUnassignedInstance = (i?: ClientInstance | null): boolean =>
  !!i && i.assignmentState === "unassigned";

/**
 * Which action un-assigns this container from its client, or null when there
 * is nothing to un-assign. Ownership has two sources, so un-assigning does too:
 *   - "unassign": HQ's override (`assigned-override`) — clears HQ's row only;
 *     the container is not touched.
 *   - "release": the container's own client LABEL (`labelled`) — labels are
 *     immutable, so the API recreates it without the label (it restarts).
 * An internal container is released on Internal Containers instead.
 */
export const unassignActionFor = (
  i?: ClientInstance | null
): "unassign" | "release" | null => {
  if (!i || isInternalInstance(i)) return null;
  if (i.assignmentState === "assigned-override") return "unassign";
  if (i.assignmentState === "labelled") return "release";
  return null;
};

/** IntraSync's own infrastructure (operator-designated) — managed in the
 *  Internal Containers view, excluded from client groupings. */
export const isInternalInstance = (i?: ClientInstance | null): boolean =>
  !!i && i.assignmentState === "internal";

export type ClientInstanceStatusFilter = "all" | "running" | "stopped";

/** Success/running statuses for a client instance. */
export const CLIENT_INSTANCE_RUNNING_STATUSES: ReadonlyArray<string> = [
  "running",
  "healthy",
  "starting",
  "restarting",
];

export const isClientInstanceRunning = (
  status?: ClientInstanceStatus
): boolean => !!status && CLIENT_INSTANCE_RUNNING_STATUSES.includes(status);

/** True when the API's stale lifecycle has marked the instance 'missing' (its
 *  container vanished from discovery). Actions are blocked on missing rows. */
export const isClientInstanceMissing = (
  status?: ClientInstanceStatus
): boolean => status === "missing";

/** The canonical ordering of the two roles in any per-client grouping. */
export const CLIENT_INSTANCE_ROLE_ORDER: ReadonlyArray<ClientInstanceRole> = [
  "api",
  "web",
];

/** A client and its (up to two) instances, for the grouped grid. */
export interface ClientInstanceGroup {
  clientId: string;
  clientName: string;
  instances: ClientInstance[];
}

/**
 * Split an image reference into registry / repository / tag. Handles registry
 * ports and digest-pinned refs: the last ":" only names a tag when it comes
 * AFTER the last "/" and is not part of an "@sha256:…" suffix. This is THE
 * web-side parser — the inline split in InstanceActionsStore.startDeploy
 * predates it; version displays ("26.7.12 → 26.8.1") must use this.
 */
export function parseImageRef(ref?: string | null): {
  registry: string | null;
  repository: string;
  tag: string | null;
} {
  const raw = String(ref ?? "").trim();
  if (!raw) return { registry: null, repository: "", tag: null };
  // A digest suffix is not a tag; strip it before looking for ":".
  const at = raw.indexOf("@");
  const withoutDigest = at >= 0 ? raw.slice(0, at) : raw;
  const lastColon = withoutDigest.lastIndexOf(":");
  const lastSlash = withoutDigest.lastIndexOf("/");
  const hasTag = lastColon > lastSlash;
  const name = hasTag ? withoutDigest.slice(0, lastColon) : withoutDigest;
  const tag = hasTag ? withoutDigest.slice(lastColon + 1) : null;
  // Docker's disambiguation rule: the first path segment is a registry host
  // only when it contains a dot or a port.
  const firstSlash = name.indexOf("/");
  const maybeHost = firstSlash > 0 ? name.slice(0, firstSlash) : "";
  const isHost = maybeHost.includes(".") || maybeHost.includes(":");
  return {
    registry: isHost ? maybeHost : null,
    repository: isHost ? name.slice(firstSlash + 1) : name,
    tag,
  };
}

/** The version a human reads off an image ref — its tag ("26.8.1"), or null. */
export const imageTagOf = (ref?: string | null): string | null =>
  parseImageRef(ref).tag;

/**
 * Display form of an image ref: the repository + tag WITHOUT the registry
 * host — `intrasync/miller-mize-api:26.8.8` rather than the full
 * `intrasyncindustrial.azurecr.io/…`. The host is the longest and least
 * informative part of every ref in a single-registry fleet; the full ref
 * belongs in a tooltip. Falls back to the raw ref when parsing yields nothing.
 */
export function displayImageRef(ref?: string | null): string {
  if (!ref) return "-";
  const { repository, tag } = parseImageRef(ref);
  if (!repository) return ref;
  return tag ? `${repository}:${tag}` : repository;
}

/** Short, safe display form of an image digest (sha256:abcd…1234). */
export function shortDigest(digest?: string | null): string {
  if (!digest) return "-";
  const hex = digest.startsWith("sha256:") ? digest.slice(7) : digest;
  if (hex.length <= 19) return digest;
  return `${digest.startsWith("sha256:") ? "sha256:" : ""}${hex.slice(0, 12)}…${hex.slice(-4)}`;
}

// ---- Container actions (Phase 5) ------------------------------------------

/**
 * The mutating control actions the UI can request against a single container.
 * Every one of these is routed through the `instance-actions` Feathers service
 * (a custom create-only action service): the web NEVER talks to Portainer/Docker
 * directly. The API resolves the host's encrypted Portainer key server-side,
 * performs the action, and writes an append-only audit row. RBAC tiers:
 *   - start / stop / restart / deploy / rollback → operator or admin
 *   - (exec is a *streaming* action, admin-only — see the exec event contract)
 * The Portainer admin key is root-equivalent and is never sent/returned here.
 */
export type InstanceActionType =
  | "start"
  | "stop"
  | "restart"
  | "deploy"
  | "rollback"
  // Phase 4 — assignment + server-resolved update-to-latest + track policy.
  | "assign"
  | "unassign"
  // Internal designation: IntraSync's own infrastructure, not a client's.
  | "mark-internal"
  | "unmark-internal"
  | "update-to-latest"
  | "set-track-policy"
  // Soft-delete a 'missing' cache row (dead duplicate left behind by an
  // out-of-band client redeploy). API refuses any non-missing row.
  | "remove"
  // Move a container to ANOTHER client: the API recreates it (same image, same
  // config) with a new com.intrasync.client-id label — so it RESTARTS. The only
  // way to move a labelled container; assign/unassign cannot (the label wins).
  | "reassign"
  // Un-assign a LABELLED container: the API recreates it (same image, same
  // config) WITHOUT its com.intrasync.client-id label — so it RESTARTS — and it
  // returns to the Unassigned pool. An override-assigned container uses
  // "unassign" instead, which only clears HQ's row.
  | "release"
  // Change the container's ENVIRONMENT (#3468, admin only): the API recreates
  // it with the same image and settings and the new variables — so it
  // RESTARTS — and puts the original back if the new one fails its health check.
  | "set-env";

/**
 * The operator gate toggle, dispatched through the same `instance-actions`
 * service (audited like every other action). 'disable' blocks all mutating
 * actions on the instance API-side; 'enable' unblocks them. operator+.
 */
export type InstanceToggleAction = "enable" | "disable";

/** Lifecycle actions (no extra payload), as opposed to deploy/rollback. */
export const LIFECYCLE_ACTIONS: ReadonlyArray<InstanceActionType> = [
  "start",
  "stop",
  "restart",
];

/** Actions that ask for confirmation before firing (destructive/disruptive). */
export const DESTRUCTIVE_ACTIONS: ReadonlyArray<InstanceActionType> = [
  "stop",
  "restart",
  "remove",
];

/**
 * The create payload posted to `instance-actions`. `instanceId` is validated by
 * the API against the persisted `client_instances` map — the client cannot
 * target an arbitrary container. `image`/`tag` (deploy) and `toDigest`
 * (rollback) are only read for those actions.
 */
export interface InstanceActionPayload {
  action: InstanceActionType | InstanceToggleAction;
  /** client_instances.id — NOT a raw container id. Omitted for the batch
   *  update-to-latest form (which targets a whole client by clientId). */
  instanceId?: string;
  /** deploy only: image repository reference (no tag), validated by the API. */
  image?: string;
  /** deploy only: image tag to pull, validated by the API. */
  tag?: string;
  /** rollback only: the prior image digest (sha256:…) to roll back to. */
  toDigest?: string;
  /** assign / reassign (the client to move to), or batch update-to-latest. */
  clientId?: string | null;
  /** assign only: optional role hint when the container carries no role label. */
  role?: string;
  /** set-track-policy only: 'same-tag' | 'semver' | 'manual' | '' (infer). */
  trackPolicy?: string;
  /** set-env only: the variables to set and to remove. */
  env?: InstanceEnvChange;
  /** set-env only: the `envVersion` of the read the change was built on. */
  envVersion?: string;
  /** The dialog's correlation token: the API echoes it on every
   *  `instance-action-progress` event of this request, and the dialog's
   *  ActionProgressWatch follows only events carrying it. */
  progressToken?: string;
}

/**
 * The result the `instance-actions` service returns (mirrors the API
 * `InstanceActionResult`). The API echoes the action + a high-level `status`
 * string and `ok` flag; it NEVER returns the host key or any Portainer
 * credential. Callers don't depend on this — they re-pull the instance record
 * for the authoritative state — so it's typed for completeness only.
 */
export interface InstanceActionResult {
  action: InstanceActionType | InstanceToggleAction | "kill";
  ok: boolean;
  instanceId: string;
  clientId?: string | null;
  /** High-level status string from the API (e.g. 'success' | 'failed'). */
  status?: string;
  /** Image transition after a deploy/rollback. */
  fromDigest?: string | null;
  toDigest?: string | null;
  imageRef?: string | null;
  /** New container id after a deploy/rollback/reassign recreate. */
  containerId?: string | null;
  /** reassign: the client the container belonged to before the move. */
  fromClientId?: string | null;
  /** reassign: env keys whose values still reference the previous client. */
  warnings?: ReassignWarning[];
  /** reassign: what Docker says the moved container is doing — `running`,
   *  `created` (it was not running before, so the move did not start it), or
   *  `unknown` (HQ could not observe it; never assumed running). */
  containerState?: string;
  /** reassign: `starting` when the moved container is running but its health
   *  check had not passed when HQ stopped watching, `unknown` when it has a
   *  health check but Docker reported no health status — both a PARTIAL success. */
  containerHealth?: string;
  /** reassign: the container was recreated under the new client's label. */
  moved?: boolean;
  /** reassign: false when the container moved but HQ's instance record was not
   *  updated — a PARTIAL success (`status: 'partial'`). */
  rowUpdated?: boolean;
  /** set-env: what changed (keys and kinds only) and how the new container did. */
  env?: {
    diff: Array<Pick<InstanceEnvDiffEntry, "key" | "change" | "masked">>;
    containerState: string;
    /** `healthy`: its health check passed. `unmeasured`: running, no health
     *  check to pass. `not-started`: the container was stopped and stays so. */
    outcome: "healthy" | "unmeasured" | "not-started";
    healthWaitedMs: number;
  };
  /** The action targeted the container serving HQ: the API handed it to a
   *  helper container (`status: 'handed-off'`). It has STARTED, not finished —
   *  the outcome is the next action-history entry (see SelfUpdateWatch). */
  handedOff?: {
    handoffId: string;
    helperName: string;
    helperContainerId: string;
    requestedAt: string;
  };
}

// ---- A container's environment (#3468, admin only) ------------------------

/** Where a variable's value comes from, compared with the image's own ENV. */
export type InstanceEnvSource = "container" | "image" | "override";

/** One variable on the Environment card. A masked value is never sent. */
export interface InstanceEnvEntry {
  key: string;
  /** The value, or null when it looks like a secret (reveal it, audited). */
  value: string | null;
  masked: boolean;
  source: InstanceEnvSource;
}

/** The `instance-env` read: the container's variables, secret values masked. */
export interface InstanceEnvRead {
  instanceId: string;
  containerName: string | null;
  imageRef: string | null;
  entries: InstanceEnvEntry[];
  /** Sent back with a preview and the apply: refuses a change built on a stale read. */
  envVersion: string;
  running: boolean;
  imageEnvKnown: boolean;
  /** Another recreate (an update, a move, a change) is under way on it now. */
  inFlight: boolean;
  /** The container serving HQ: listed, never revealed or changed from HQ. */
  selfContainer: boolean;
  /** Actions on this instance are blocked by the operator gate. */
  disabled: boolean;
  /** HQ keeps no standard set of variables per client: always null today. */
  standard: null;
}

/** What a person asked to change. Values are the only secrets in it. */
export interface InstanceEnvChange {
  set: Array<{ key: string; value: string }>;
  remove: string[];
}

/** How one variable will change; values only for keys that do not look secret. */
export interface InstanceEnvDiffEntry {
  key: string;
  change: "added" | "changed" | "removed" | "reset-to-image";
  masked: boolean;
  before?: string | null;
  after?: string | null;
}

/** The `instance-env` preview of a change. */
export interface InstanceEnvPreview {
  instanceId: string;
  envVersion: string;
  diff: InstanceEnvDiffEntry[];
  /** The container restarts (it is running) or is recreated stopped. */
  restarts: boolean;
}

/**
 * What became of the ORIGINAL container when a recreate (deploy, rollback,
 * move) failed — the API sends it on the error as `data.recovery`:
 *   - `not-needed`     nothing destructive happened;
 *   - `restored`       HQ put the original container back;
 *   - `restore-failed` HQ could NOT confirm the original is back — the
 *                      instance may be DOWN.
 */
export type RecreateRecovery = "not-needed" | "restored" | "restore-failed";

export const RECREATE_RECOVERIES: ReadonlyArray<RecreateRecovery> = [
  "not-needed",
  "restored",
  "restore-failed",
];

/**
 * One env variable a `reassign` found still referencing the PREVIOUS client
 * (its id, or its code as a token). The API never sends the value — env holds
 * secrets — only the key and a sentence naming it. HQ does not rewrite env;
 * this is what the operator must go and fix by hand.
 */
export interface ReassignWarning {
  envKey: string;
  matches: Array<"client-id" | "client-code">;
  message: string;
}

// ---- Action audit / deploy history (Phase 5, read-only) --------------------

/**
 * One append-only audit row from the `instance-action-audit` read service — the
 * deploy/action history for an instance. Mirrors the `instance_action_audit`
 * table exactly. Every mutating action (start/stop/restart/kill/deploy/rollback/
 * exec) writes one row recording WHO (`actorUserId`), WHAT (`action`), on WHICH
 * container (`instanceId`/`clientId`), the image transition (`fromDigest` ->
 * `toDigest`, `imageRef`), and a REDACTED `detail`. NO secret is ever stored.
 */
export interface InstanceActionAudit {
  id: string;
  /** NULLable: the container may have been removed mid-action. */
  instanceId?: string | null;
  clientId?: string | null;
  /** Which action was performed (start/stop/restart/kill/deploy/rollback/exec). */
  action: InstanceActionType | "kill" | "exec" | string;
  /** Outcome status: 'success' | 'failed' | 'started'. */
  status?: "success" | "failed" | "started" | string;
  /** Acting user (users.id is INT); NULL for internal/system calls. */
  actorUserId?: number | string | null;
  /** Image transition (old/new container image digests). */
  fromDigest?: string | null;
  toDigest?: string | null;
  /** Human image reference involved (name:tag). */
  imageRef?: string | null;
  /** The image the container ran BEFORE the action (deploy, rollback,
   *  update-to-latest, reassign/release, observed-change); null on older rows. */
  fromImageRef?: string | null;
  /** A client deploy/rollback's watched result, and an observed change's:
   *  `healthy` | `unhealthy-after-deploy` | `unmeasured`. Null otherwise. */
  outcome?: "healthy" | "unhealthy-after-deploy" | "unmeasured" | string | null;
  /** Redacted free-text detail (e.g. the exec command summary). */
  detail?: string | null;
  /** Set on every entry of one self-update (the hand-off of the container
   *  serving HQ to a helper); null otherwise. */
  handoffId?: string | null;
  createdAt?: string;
}

/** Audit statuses that represent a successful outcome. */
export const AUDIT_OK_STATUSES: ReadonlyArray<string> = ["success", "started"];

/** A prior digest a rollback can target, derived from the audit history. */
export interface InstanceDigestOption {
  /** The image digest (sha256:…) to roll back to. */
  digest: string;
  /** The image reference (name:tag) that produced this digest, if known. */
  image?: string | null;
  /** When this digest was last deployed (most recent first in the list). */
  deployedAt?: string | null;
}

// ---- Live streaming event contract (Phase 5) ------------------------------
//
// STAFF-ONLY streams. These are raw Socket.io events (NOT Feathers CRUD events),
// emitted/received on the shared authenticated socket. They are never delivered
// to portal `client/<id>` channels — only to authenticated staff sockets. Each
// stream MUST be torn down on unsubscribe AND on socket disconnect, and the API
// caps concurrent streams per user. instanceId is validated against the
// persisted client_instances map; exec args are passed as an array, never shell-
// interpolated. The exec session is admin-only.

/** Log stream wire events. */
export const INSTANCE_LOGS_SUBSCRIBE = "instance:logs:subscribe";
export const INSTANCE_LOGS_UNSUBSCRIBE = "instance:logs:unsubscribe";
export const INSTANCE_LOGS_DATA = "instance:logs:data";

/** Stats stream wire events. */
export const INSTANCE_STATS_SUBSCRIBE = "instance:stats:subscribe";
export const INSTANCE_STATS_UNSUBSCRIBE = "instance:stats:unsubscribe";
export const INSTANCE_STATS_DATA = "instance:stats:data";

/**
 * Exec session wire events (admin only). NOTE the asymmetry, which matches the
 * API streaming handler exactly:
 *   - the client EMITS `instance:exec:start` / `:input` / `:resize` / `:stop`;
 *   - the API EMITS `instance:exec:ready` (PTY open) / `:output` / `:end`
 *     (process closed) back. There is NO sessionId — a container has at most
 *     one exec stream keyed by instanceId server-side; the resize relay
 *     resolves the Docker execId from that server-side map (never the client).
 */
export const INSTANCE_EXEC_START = "instance:exec:start";
export const INSTANCE_EXEC_INPUT = "instance:exec:input";
export const INSTANCE_EXEC_RESIZE = "instance:exec:resize";
export const INSTANCE_EXEC_STOP = "instance:exec:stop";
export const INSTANCE_EXEC_READY = "instance:exec:ready";
export const INSTANCE_EXEC_OUTPUT = "instance:exec:output";
export const INSTANCE_EXEC_END = "instance:exec:end";

// ---- Fleet status feed (periodic reconcile sweep) ---------------------------
//
// The API's reconcile sweep re-discovers the fleet every few minutes and emits
// ONE custom Feathers service event with the refreshed record list (the same
// contract `client-instances` find() returns). Like the DevOps feed, the wire
// name is the Feathers "<servicePath> <event>" form — listening on the bare
// event name silently receives nothing. STAFF-ONLY (internal channel).

/** Wire event name for the periodic fleet-status fan-out. */
export const INSTANCE_STATUS_EVENT = "client-instances instance-status";

/** Wire event name for the registry sweep's update-availability fan-out. */
export const INSTANCE_UPDATE_EVENT = "client-instances instance-update-available";

/** Envelope the registry sweep publishes on {@link INSTANCE_UPDATE_EVENT}. */
export interface InstanceUpdateEnvelope {
  at?: string;
  instances?: Array<{
    instanceId: string;
    clientId?: string | null;
    latestImageRef?: string | null;
  }>;
}

/** Envelope the sweep publishes on {@link INSTANCE_STATUS_EVENT}. */
export interface InstanceStatusEnvelope {
  at?: string;
  /** The full refreshed fleet (live rows + surviving 'missing' rows). */
  instances?: ClientInstance[];
}

/**
 * Wire event name for the per-step progress of ONE container action, emitted
 * by the API's instance-actions service for the dialog that started it
 * (`packages/api/src/services/infrastructure/instance-actions/action-progress.ts`).
 * Same "<servicePath> <event>" form as the other two; staff channel.
 */
export const INSTANCE_ACTION_PROGRESS_EVENT =
  "client-instances instance-action-progress";

/**
 * The step an action is on. Emitted when the step is about to be SENT, not
 * when it has succeeded; the run ends with `finished`, `failed`, or
 * `handed-off` (the container serves HQ — SelfUpdateWatch follows the helper).
 * Kept in step with the API's `ActionProgressPhase` by hand.
 */
export type InstanceActionPhase =
  | "resolving"
  | "inspecting"
  | "pulling"
  | "stopping"
  | "removing"
  | "creating"
  | "starting"
  | "restarting"
  | "health"
  | "restoring"
  | "handed-off"
  | "finished"
  | "failed";

/** Image-pull counts the API derives from Docker's pull stream. */
export interface InstanceActionPullProgress {
  layersDone: number;
  layersTotal: number;
  bytesDone: number;
  bytesTotal: number;
}

/** One event on {@link INSTANCE_ACTION_PROGRESS_EVENT}. Phases, counts and
 *  ids only — the API never puts container data on it. */
export interface InstanceActionProgressEvent {
  at: string;
  /** The instance id AS THE REQUESTER SENT IT (compare case-insensitively
   *  anyway: GUIDs). */
  instanceId: string;
  /** The API's id for one run of one action (one request). */
  runId: string;
  /** The requester's `progressToken`, echoed verbatim; absent when the
   *  request carried none. The dialog follows the run with ITS token. */
  token?: string;
  /** The action as requested (`update-to-latest`, not the deploy it runs). */
  action: string;
  phase: InstanceActionPhase;
  /** Order within the run, from 1. */
  seq: number;
  pull?: InstanceActionPullProgress;
  /** `health`: the most the API will wait before it gives a verdict. */
  waitUpToMs?: number;
  /** Steps this run will NOT take, known at this point (a stopped container
   *  is recreated stopped: no `starting`, no `health`). */
  skips?: InstanceActionPhase[];
}

/**
 * A single chunk of container log output, as emitted by the API on
 * `instance:logs:data`: `{ instanceId, stream, line }`. `line` is the raw log
 * text (may contain ANSI; xterm renders it). `stream` demuxes stdout/stderr.
 */
export interface InstanceLogData {
  instanceId: string;
  line: string;
  stream?: "stdout" | "stderr" | string;
}

/**
 * A single container stats sample as emitted on `instance:stats:data`:
 * `{ instanceId, cpu, mem: { usage, limit }, net: { rx, tx } }`. The API
 * pre-computes cpu% and the byte counts from the Docker stats frame.
 */
export interface InstanceStatsData {
  instanceId: string;
  /** CPU usage percent (already rounded; can exceed 100 across cores). */
  cpu?: number;
  mem?: { usage?: number; limit?: number };
  net?: { rx?: number; tx?: number };
}

/** Exec output chunk emitted by the API on `instance:exec:output`. */
export interface InstanceExecOutput {
  instanceId: string;
  /** Raw TTY bytes as a UTF-8 string. */
  data: string;
}

/**
 * The API's single error channel for streaming failures (auth denial, "already
 * subscribed", concurrent-stream cap, instance-not-found, transport errors).
 * `kind` says which stream the error belongs to.
 */
export const INSTANCE_ERROR = "instance:error";

export interface InstanceStreamError {
  kind: "logs" | "stats" | "exec" | string;
  message: string;
}

/**
 * The payload the client sends to start an exec session. The command is an
 * ARRAY of argv tokens under `cmd` — never a shell string — so nothing is
 * shell-interpolated server-side (the API reads `payload.cmd`).
 */
export interface InstanceExecStartPayload {
  instanceId: string;
  /** argv, e.g. ["/bin/sh"]. The API sanitises + defaults to a shell. */
  cmd: string[];
}

// ---- Stats sample accessors -----------------------------------------------
// The API sample is `{ cpu, mem: { usage, limit }, net: { rx, tx } }`; these
// derive the display numbers (percent / bytes) the panel + sparklines use so
// the wire shape lives in exactly one place.

export function statCpuPercent(s?: InstanceStatsData | null): number {
  return s?.cpu ?? 0;
}

export function statMemUsedBytes(s?: InstanceStatsData | null): number | undefined {
  return s?.mem?.usage;
}

export function statMemLimitBytes(
  s?: InstanceStatsData | null
): number | undefined {
  return s?.mem?.limit;
}

export function statMemPercent(s?: InstanceStatsData | null): number {
  const usage = s?.mem?.usage;
  const limit = s?.mem?.limit;
  if (!usage || !limit) return 0;
  return Math.min(100, (usage / limit) * 100);
}

export function statNetRxBytes(s?: InstanceStatsData | null): number | undefined {
  return s?.net?.rx;
}

export function statNetTxBytes(s?: InstanceStatsData | null): number | undefined {
  return s?.net?.tx;
}

/** Human-readable bytes (for the stats panel). */
export function formatBytes(bytes?: number | null): string {
  if (bytes == null || Number.isNaN(bytes)) return "-";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

/** The default exec shell argv tried first (the API may fall back to /bin/sh). */
export const DEFAULT_EXEC_COMMAND: ReadonlyArray<string> = ["/bin/sh"];
